import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Paperclip, Loader2, X, FileText } from 'lucide-react';

const MAX_FILES = 5;
const MAX_TOTAL = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx';
const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx'];

export const formatBytes = (n) => {
  if (!n) return '0 KB';
  return n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const NewsletterAttachments = ({ attachments, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const total = attachments.reduce((s, a) => s + a.size, 0);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`You can attach up to ${MAX_FILES} files`);
      return;
    }
    const bad = files.find((f) => !ALLOWED_EXT.includes(f.name.split('.').pop().toLowerCase()));
    if (bad) {
      toast.error(`"${bad.name}" is not a supported type (PDF, JPG, PNG, DOCX, XLSX)`);
      return;
    }
    if (total + files.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) {
      toast.error('Attachments must be 10 MB or less in total');
      return;
    }
    setUploading(true);
    const token = localStorage.getItem('ams_admin_token');
    const uploaded = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletters/upload-attachment`,
          fd,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { filename, storage_path, content_type, size } = res.data;
        uploaded.push({ filename, storage_path, content_type, size });
      }
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? 's' : ''} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      if (uploaded.length) onChange([...attachments, ...uploaded]);
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid="newsletter-attachments-section">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-2">
          <Paperclip className="w-4 h-4" />
          Attachments
        </Label>
        <span className="text-xs text-muted-foreground" data-testid="newsletter-attachments-usage">
          {attachments.length}/{MAX_FILES} files &middot; {formatBytes(total)} of 10 MB
        </span>
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1.5" data-testid="newsletter-attachments-list">
          {attachments.map((a, i) => (
            <li key={a.storage_path} className="flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm" data-testid={`newsletter-attachment-item-${i}`}>
              <FileText className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate flex-1">{a.filename}</span>
              <span className="text-xs text-muted-foreground">{formatBytes(a.size)}</span>
              <button
                type="button"
                onClick={() => onChange(attachments.filter((_, j) => j !== i))}
                className="text-muted-foreground hover:text-destructive transition-colors"
                aria-label={`Remove ${a.filename}`}
                data-testid={`newsletter-attachment-remove-${i}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="newsletter-attachment-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading || attachments.length >= MAX_FILES}
        onClick={() => inputRef.current?.click()}
        data-testid="newsletter-add-attachment-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
        {uploading ? 'Uploading...' : 'Add attachment'}
      </Button>
      <p className="text-xs text-muted-foreground">PDF, JPG, PNG, DOCX or XLSX. Sent as real file attachments to every recipient.</p>
    </div>
  );
};
